import { UnfoldMore } from '@mui/icons-material';
import { Avatar, Box, ButtonBase, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { useUser } from '../../hooks/useUser';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/store';
import { hasOneNewPolicy } from '../../utils/policyDocumentUtils';
import ShieldPerson from '../../custom-icons/ShieldPerson';
import EmailIcon from '../../custom-icons/EmailIcon';
import SettingsIcon from '../../custom-icons/SettingsIcon';
import LogOutIcon from '../../custom-icons/LogOutIcon';

import { t } from '@lingui/core/macro';
export type ProfileMenuProps = {
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
  size: 'small' | 'large';
};

const ProfileMenu = ({ onNavigate, onLogout, size }: ProfileMenuProps) => {
  const { auth: user } = useUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const policyDocument = useSelector((state: AppState) => state.policyDocument);
  const dispatch = useDispatch();
  const { palette } = useTheme();

  const isProjectAdmin = hasOneNewPolicy(
    policyDocument,
    [
      'Projects_Admin',
      'Projects_MotivePowerAdmin',
      'Projects_TagAdmin',
      'Projects_ViewAdmin',
      'Projects_TeamAdmin',
    ],
    'all'
  );

  const stringToAvatarName = (name: string) => {
    const nameParts = name.split(' ');
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return name[0];
  };

  const handleNavigate = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT_USER' });
    /* signOutExchangeToken(); */
    navigate('/login');
  };

  const handleOnLogout = () => {
    setAnchorEl(null);
    if (onLogout) {
      onLogout();
    } else {
      handleLogout();
    }
  };

  const handleButtonBaseClicked = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleContactSupport = () => {
    window.location.href = `mailto:strytencustomersupport@stryten.com?subject=${encodeURIComponent(
      t`inCOMMAND Customer Support`
    )}&body=${encodeURIComponent(
      t`Hello inCOMMAND team,

(Briefly describe what we can help with).

Project Name (if relevant):
Date or Time issue occurred:
Browser/OS:

Thanks,
${user?.first_name} ${user?.last_name}`
    )}`;
  };

  const renderBasicInfo = useCallback(
    (_size?: string) => {
      return (
        <Box
          display="flex"
          sx={{
            overflow: 'hidden',
            flex: 1,
            alignItems: 'center',
          }}
        >
          <Avatar
            sx={{
              bgcolor: palette.divider2,
              color: palette.text.secondary,
              width: '32px',
              height: '32px',
              minWidth: '32px',
              flexShrink: 0,
            }}
          >
            <Typography color="text.secondary" variant="body2">
              {stringToAvatarName(user?.first_name + ' ' + user?.last_name)}
            </Typography>
          </Avatar>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginLeft: _size === 'small' ? 0 : '12px',
              textAlign: 'left',
              overflow: 'hidden',
              clipPath: _size === 'small' ? 'inset(0 100% 0 0)' : 'inset(0 0% 0 0)',
              transition: 'clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.4s ease',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography color="text.secondary" variant="body1">
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography color="text.tertiary" variant="caption1" noWrap>
              {user?.email}
            </Typography>
          </Box>
        </Box>
      );
    },
    [palette.divider2, palette.text.secondary, user?.email, user?.first_name, user?.last_name]
  );

  return (
    <>
      <ButtonBase
        id="basic-button"
        aria-controls={anchorEl ? 'profileMenu' : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? 'true' : undefined}
        sx={{
          display: 'flex',
          width: '100%',
          borderRadius: '4px',
          padding: '12px 11px',
          border: `1px solid ${anchorEl ? palette.text.secondary : 'transparent'}`,
          pointerEvents: 'visible',
          backgroundColor: anchorEl ? palette.background.default : 'transparent',
        }}
        onClick={handleButtonBaseClicked}
      >
        <Box display="flex" alignItems="center" width="100%">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {renderBasicInfo(size)}
          </Box>
          <UnfoldMore
            sx={{
              color: anchorEl ? 'text.primary' : 'text.secondary',
              opacity: size === 'small' ? 0 : 1,
              width: size === 'small' ? '0px' : '24px',
              transition: 'width 0.35s ease',
              flexShrink: 0,
            }}
          />
        </Box>
      </ButtonBase>
      <Menu
        id="profileMenu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              left: '8px !important',
              width: size === 'large' ? anchorEl?.offsetWidth : '256px',
            },
          },
          list: {
            sx: {
              padding: '4px 0px',
            },
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.default',

            borderRadius: '4px',
            border: anchorEl ? `1px solid ${palette.text.secondary}` : 'none',
          }}
        >
          <Box>
            {/*<Box>{renderBasicInfo()}</Box>
            <Divider sx={{ margin: "8px 0" }} />*/}
            {
              // prettier-ignore
              [
                {id: "logout",label: t`Log Out`,onClick:() => handleOnLogout(),icon: LogOutIcon,},
                {id: "admin",label: t`Admin Console`,hidden: !isProjectAdmin,onClick: () => {handleNavigate("admin");}, icon: ShieldPerson, },
                {id: "settings",label: t`Settings`,onClick: () => handleNavigate("user-settings"),icon: SettingsIcon,},
                {id: "support",label: t`Customer Support`,onClick: () => handleContactSupport(),icon: EmailIcon,},

          ].map((item) =>
            !item.hidden ? (
              <MenuItem
                key={item.id}
                onClick={() => {
                  setAnchorEl(null);
                  item.onClick();
                }}
                sx={{ display: "flex", alignItems: "center", height: "40px", color: palette.text.secondary,
                  '&:hover': { color: palette.text.primary },
                 }}
              >
                <item.icon style={{ marginRight: "8px" }} />
                {item.label}
              </MenuItem>
            ) : null
          )
            }
          </Box>
          {/*<Divider sx={{ margin: "8px 0" }} />
          <Box
            sx={{
              padding: "0 8px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                setAnchorEl(null);
                onLogout?.() ?? handleLogout();
              }}
            >
              Logout
            </Button>
          </Box> */}
        </Box>
        {/* Additional menu items can be added here */}
      </Menu>
    </>
  );
};

export default ProfileMenu;
